import type { TFunction } from 'react-i18next';
import * as yup from 'yup';

import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

import { isLegitimateInterestsBasis } from './processingActivityEditor.parsers';
import type { ProcessingActivityEditorFormValues, ProcessingActivityEditorPurpose } from './processingActivityEditor.types';

export type ProcessingActivityEditorValidationErrors = Partial<Record<string, string>>;

export type ProcessingActivityEditorValidationMessages = {
  required: string;
  categoryRequired: string;
  purposeRequired: string;
  purposeIncomplete: string;
  legalBasisCommentRequired: string;
  retentionValueMin: string;
  purposeRetentionInvalid: string;
};

export const buildDraftValidationSchema = () =>
  yup.object({
    name: yup.string().trim().max(255),
    description: yup.string(),
    processingRole: yup.string().oneOf(['controller', 'processor', '']),
    minimumSecurityMeasures: yup.string(),
    dataSubjectCategories: yup.array(),
    personalDataCategories: yup.array(),
    recipientCategories: yup.array(),
    purposes: yup.array(),
  });

const isLegalBasisCommentMissing = (purpose: ProcessingActivityEditorPurpose): boolean =>
  isLegitimateInterestsBasis(purpose.legalBasis) && !purpose.legalBasisComment.trim();

export type PurposeDialogValidationErrors = Partial<
  Record<'purposeText' | 'legalBasis' | 'legalBasisComment' | 'retentionTrigger' | 'retentionValue' | 'retentionUnit', string>
>;

const isBlank = (value: string): boolean => !value.trim();

const isRetentionValueMissing = (value: number | null): value is null => value == null;

export const isRetentionValueInvalid = (value: number | null): boolean => value != null && (!Number.isInteger(value) || value < 1);

export const getPurposeRetentionErrorKey = (clientId: string): string => `purpose.${clientId}.retentionValue`;

export const hasInvalidPurposeRetention = (errors: ProcessingActivityEditorValidationErrors): boolean =>
  Object.keys(errors).some(key => key.endsWith('.retentionValue'));

export const validatePurposeForDialog = (
  purpose: ProcessingActivityEditorPurpose,
  messages: Pick<ProcessingActivityEditorValidationMessages, 'legalBasisCommentRequired' | 'required' | 'retentionValueMin'>,
): PurposeDialogValidationErrors => {
  const errors: PurposeDialogValidationErrors = {};

  if (isBlank(purpose.purposeText)) {
    errors.purposeText = messages.required;
  }

  if (!purpose.legalBasis) {
    errors.legalBasis = messages.required;
  }

  if (isLegalBasisCommentMissing(purpose)) {
    errors.legalBasisComment = messages.legalBasisCommentRequired;
  }

  if (isBlank(purpose.retentionTrigger)) {
    errors.retentionTrigger = messages.required;
  }

  if (isRetentionValueMissing(purpose.retentionValue)) {
    errors.retentionValue = messages.required;
  } else if (isRetentionValueInvalid(purpose.retentionValue)) {
    errors.retentionValue = messages.retentionValueMin;
  }

  if (!purpose.retentionUnit) {
    errors.retentionUnit = messages.required;
  }

  return errors;
};

type PurposePublishIssue = 'retention' | 'lia' | 'incomplete';

const getPurposePublishIssue = (purpose: ProcessingActivityEditorPurpose): PurposePublishIssue | null => {
  if (purpose.isSoftDeleted) return null;

  const missingRequiredFields =
    isBlank(purpose.purposeText) ||
    !purpose.legalBasis ||
    isBlank(purpose.retentionTrigger) ||
    isRetentionValueMissing(purpose.retentionValue) ||
    !purpose.retentionUnit;
  const hasInvalidRetention = isRetentionValueInvalid(purpose.retentionValue);

  if (hasInvalidRetention && !missingRequiredFields && !isLegalBasisCommentMissing(purpose)) {
    return 'retention';
  }

  if (isLegalBasisCommentMissing(purpose)) {
    return 'lia';
  }

  if (missingRequiredFields || hasInvalidRetention) {
    return 'incomplete';
  }

  return null;
};

export const buildProcessingActivityEditorValidationMessages = (t: TFunction): ProcessingActivityEditorValidationMessages => ({
  required: t(validation_messages.required),
  categoryRequired: t(processing_activities_messages.editor.validation.category_required),
  purposeRequired: t(processing_activities_messages.editor.validation.purpose_required),
  purposeIncomplete: t(processing_activities_messages.editor.validation.purpose_incomplete),
  legalBasisCommentRequired: t(processing_activities_messages.editor.validation.legal_basis_comment_required),
  retentionValueMin: t(processing_activities_messages.editor.validation.retention_value_min),
  purposeRetentionInvalid: t(processing_activities_messages.editor.validation.purpose_retention_invalid),
});

export const validateProcessingActivityEditorForPublish = (
  values: ProcessingActivityEditorFormValues,
  messages: ProcessingActivityEditorValidationMessages,
): ProcessingActivityEditorValidationErrors => {
  const errors: ProcessingActivityEditorValidationErrors = {};

  if (!values.name.trim()) {
    errors.name = messages.required;
  }

  if (!values.description.trim()) {
    errors.description = messages.required;
  }

  if (!values.processingRole) {
    errors.processingRole = messages.required;
  }

  if (!values.minimumSecurityMeasures.trim()) {
    errors.minimumSecurityMeasures = messages.required;
  }

  if (values.dataSubjectCategories.length === 0) {
    errors.dataSubjectCategories = messages.categoryRequired;
  }

  if (values.personalDataCategories.length === 0) {
    errors.personalDataCategories = messages.categoryRequired;
  }

  if (values.recipientCategories.length === 0) {
    errors.recipientCategories = messages.categoryRequired;
  }

  const activePurposes = values.purposes.filter(purpose => !purpose.isSoftDeleted);

  if (activePurposes.length === 0) {
    errors.purposes = messages.purposeRequired;
  } else {
    activePurposes.forEach(purpose => {
      if (isRetentionValueInvalid(purpose.retentionValue)) {
        errors[getPurposeRetentionErrorKey(purpose.clientId)] = messages.purposeRetentionInvalid;
      }
    });

    const issues = activePurposes.map(getPurposePublishIssue);
    if (issues.includes('retention')) {
      errors.purposes = messages.purposeRetentionInvalid;
    } else if (issues.includes('lia')) {
      errors.purposes = messages.legalBasisCommentRequired;
    } else if (issues.includes('incomplete')) {
      errors.purposes = messages.purposeIncomplete;
    }
  }

  return errors;
};

export const hasValidationErrors = (errors: ProcessingActivityEditorValidationErrors) => Object.keys(errors).length > 0;

export const buildPublishDisabledTooltip = (errors: ProcessingActivityEditorValidationErrors): string =>
  Array.from(new Set(Object.values(errors).filter((message): message is string => Boolean(message)))).join('\n');
